/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { Fragment } from 'react'
import { capitalize } from 'lodash'
import { Link } from 'react-router-dom'

import {
  EuiOverlayMask,
  EuiModal,
  EuiModalHeader,
  EuiFlexItem,
  EuiFlexGroup,
  EuiText,
  EuiModalBody,
  EuiSpacer,
  EuiModalFooter,
  EuiLink,
  EuiAccordion,
  useEuiTheme,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { DocLink } from '@modules/doclinks'
import useGetFeaturesUsage from '@modules/billing-lib/queries/useGetFeaturesUsage'
import type { BillingSubscriptionLevel } from '@modules/ui-types/profile'
import useOrganizationId from '@modules/billing-lib/hooks/useOrganizationId'

import { getOutOfComplianceDeployments } from '@/lib/usage'
import CustomerOnlyAction from '@/components/User/CustomerOnlyAction'
import { supportUrl } from '@/lib/urlBuilder'

import DeploymentUsage from './DeploymentUsage'
import DeploymentFeaturesAboveSubscriptionLevel from './DeploymentFeaturesAboveSubscriptionLevel'
import messages from './messages'

import type { ReactElement } from 'react'

interface Props {
  closeModal: () => void
  onUpgrade: (upgradeLevel?: BillingSubscriptionLevel) => void
}

const ReviewSubscriptionChangesModal = ({ onUpgrade, closeModal }: Props) => {
  const { euiTheme } = useEuiTheme()
  const organizationId = useOrganizationId()
  const featureUsageQuery = useGetFeaturesUsage(
    {
      organizationId: organizationId!,
    },
    { enabled: Boolean(organizationId) },
  )
  const modalTitleId = useGeneratedHtmlId()

  if (!organizationId || !featureUsageQuery.data) {
    return null
  }

  const { subscription_level, usage_level, deployments } = featureUsageQuery.data

  const renderSingleDeployment = ({ deployment }): ReactElement => {
    const { outOfComplianceFeatures } = deployment
    return (
      <Fragment>
        <DeploymentUsage
          outOfComplianceFeatures={outOfComplianceFeatures}
          deployment={deployment}
        />
        <DeploymentFeaturesAboveSubscriptionLevel
          outOfComplianceFeatures={outOfComplianceFeatures}
        />
      </Fragment>
    )
  }

  const renderCollapsedDeployment = ({ deployment }): ReactElement => {
    const { outOfComplianceFeatures } = deployment
    return (
      <EuiAccordion
        id='features-by-deployment'
        buttonContent={
          <DeploymentUsage
            outOfComplianceFeatures={outOfComplianceFeatures}
            deployment={deployment}
          />
        }
      >
        <DeploymentFeaturesAboveSubscriptionLevel
          outOfComplianceFeatures={outOfComplianceFeatures}
        />
      </EuiAccordion>
    )
  }

  const renderDeploymentUsage = (): ReactElement | null => {
    const outOfComplianceDeployments = getOutOfComplianceDeployments({
      deployments,
      subscriptionLevel: subscription_level,
    })

    if (outOfComplianceDeployments.length < 1) {
      return null
    }

    return outOfComplianceDeployments.map((deployment) => (
      <Fragment key={deployment.id}>
        {outOfComplianceDeployments.length === 1
          ? renderSingleDeployment({ deployment })
          : renderCollapsedDeployment({ deployment })}
        <EuiSpacer size='m' />
      </Fragment>
    ))
  }

  return (
    <EuiOverlayMask>
      <EuiModal
        aria-labelledby={modalTitleId}
        maxWidth={euiTheme.breakpoint.s}
        onClose={closeModal}
      >
        <EuiModalHeader>
          <EuiFlexGroup gutterSize='s' direction='column'>
            <EuiFlexItem>
              <EuiText>
                <h3 id={modalTitleId}>
                  <FormattedMessage {...messages.modalTitle} />
                </h3>
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiText color='subdued'>
                <p>
                  <FormattedMessage
                    {...messages.modalDescription}
                    values={{
                      subscription: capitalize(subscription_level),
                      upgradeToLevel: (
                        <CustomerOnlyAction>
                          <EuiLink
                            onClick={() => onUpgrade(usage_level as BillingSubscriptionLevel)}
                          >
                            <FormattedMessage
                              {...messages.upgrade}
                              values={{
                                upgradeLevel: capitalize(usage_level),
                              }}
                            />
                          </EuiLink>
                        </CustomerOnlyAction>
                      ),
                    }}
                  />
                </p>
              </EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiModalHeader>
        <EuiModalBody>{renderDeploymentUsage()}</EuiModalBody>
        <EuiModalFooter css={css({ justifyContent: 'flex-start' })}>
          <EuiText>
            <FormattedMessage
              {...messages.modalFooter}
              values={{
                readTheDocs: (
                  <DocLink link='subscriptionLevels'>
                    <FormattedMessage {...messages.readTheDocs} />
                  </DocLink>
                ),
                askForHelp: (
                  <CustomerOnlyAction>
                    <Link to={supportUrl()} target='_blank'>
                      <FormattedMessage {...messages.askForHelp} />
                    </Link>
                  </CustomerOnlyAction>
                ),
              }}
            />
          </EuiText>
        </EuiModalFooter>
      </EuiModal>
    </EuiOverlayMask>
  )
}

export default ReviewSubscriptionChangesModal
